/**
 * 숫자를 1000 단위마다 콤마를 추가하여 문자열로 반환하는 함수
 *
 * @example formatCommaNumber(1000000) => '1,000,000'
 *
 * @param {number|string} number 숫자
 *
 * @returns {string} 1000 단위로 콤마가 추가된 문자열
 */
export const formatCommaNumber = (number: number | string): string => {
  // undefined, null, 빈 문자열 처리
  if (number === undefined || number === null || number === "") {
    return "0";
  }

  if (isNaN(Number(number))) {
    console.warn("Invalid number provided:", number);
    return "0";
  }

  return Number(number).toLocaleString();
};

/**
 * 전화번호를 포맷팅하는 함수
 *
 * @example formatPhoneNumber('01012345678') => '010-1234-5678'
 * @example formatPhoneNumber('0212345678') => '02-1234-5678'
 * @example formatPhoneNumber('021234567') => '02-123-4567'
 *
 * @param {string | null | undefined} phone 전화번호
 *
 * @returns {string} 하이픈이 추가된 문자열 or "-"
 */
export const formatPhoneNumber = (phone?: string | null): string => {
  if (!phone) return "-"; // null, undefined, 빈 문자열 안전 처리

  // 마스킹된 전화번호 처리 (예: 01099**71**)
  if (phone.includes("*")) {
    // 숫자와 *만 추출
    const masked = phone.replace(/[^\d*]/g, "");
    // 01099**71** → 010-99**-71**
    if (masked.length === 9) {
      return masked.replace(/(\d{3})(\d{2}\*{2})(\d{2}\*{2})/, "$1-$2-$3");
    }
    // 01099**71* → 010-99**-71*
    if (masked.length === 10) {
      return masked.replace(/(\d{3})(\d{2}\*{2})(\d{3}\*?)/, "$1-$2-$3");
    }
    // 01099**711 → 010-99**-711
    if (masked.length === 10) {
      return masked.replace(/(\d{3})(\d{2}\*{2})(\d{3})/, "$1-$2-$3");
    }
    // 기타: 01099**71** 등 다양한 마스킹 패턴이 있을 수 있으니, 3-나머지-4로 시도
    if (masked.length > 7) {
      return masked.replace(/(\d{3})(.+)(\d{2,4})/, "$1-$2-$3");
    }
    return phone;
  }

  const onlyNumbers = phone.replace(/\D/g, "");

  // 11자리 (휴대폰: 010-1234-5678)
  if (onlyNumbers.length === 11) {
    return onlyNumbers.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
  }

  // 10자리 처리
  if (onlyNumbers.length === 10) {
    // 지역번호로 시작하는 경우 (02, 031, 032, 041, 042, 043, 044, 051, 052, 053, 054, 055, 061, 062, 063, 064)
    // 02로 시작: 02-1234-5678
    if (onlyNumbers.startsWith("02")) {
      return onlyNumbers.replace(/(\d{2})(\d{4})(\d{4})/, "$1-$2-$3");
    }
    // 3자리 지역번호로 시작: 031-123-4567 또는 031-1234-5678
    // 일반적으로 031, 032, 041, 042, 043, 044, 051, 052, 053, 054, 055, 061, 062, 063, 064
    if (
      /^(031|032|041|042|043|044|051|052|053|054|055|061|062|063|064)/.test(
        onlyNumbers
      )
    ) {
      return onlyNumbers.replace(/(\d{3})(\d{3,4})(\d{4})/, "$1-$2-$3");
    }
    // 기타 10자리: 2-3-4 또는 3-3-4 패턴 시도
    return onlyNumbers.replace(/(\d{2,3})(\d{3,4})(\d{4})/, "$1-$2-$3");
  }

  // 9자리 (지역번호: 02-123-4567)
  if (onlyNumbers.length === 9) {
    // 02로 시작하는 경우
    if (onlyNumbers.startsWith("02")) {
      return onlyNumbers.replace(/(\d{2})(\d{3})(\d{4})/, "$1-$2-$3");
    }
    // 기타 9자리: 2-3-4 패턴
    return onlyNumbers.replace(/(\d{2})(\d{3})(\d{4})/, "$1-$2-$3");
  }

  return phone;
};

/**
 * 생년월일 자동 - 추가
 *
 * @example formatBirthday('19900101') => '1990-01-01'
 *
 * @param {string | null | undefined} birthday 생년월일
 *
 * @returns {string} 하이픈이 추가된 문자열 or "-"
 */
export const formatBirthday = (value: string): string => {
  // 빈 문자열이면 빈 문자열 반환 (지울 수 있도록)
  if (!value) return "";

  // 숫자만 추출
  let numbers = value.replace(/[^0-9]/g, "");

  // 8자리까지만 허용
  if (numbers.length > 8) {
    numbers = numbers.slice(0, 8);
  }

  // 자동으로 - 추가
  if (numbers.length > 6) {
    // 7자리 이상일 때만 두 번째 하이픈 추가
    return `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(
      6,
      8
    )}`;
  } else if (numbers.length > 4) {
    // 5-6자리일 때 첫 번째 하이픈만 추가
    return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
  }
  // 4자리 이하일 때는 하이픈 추가하지 않음

  return numbers;
};

/**
 * 청구서 발송 가능 건수 표시. 발송 단가가 0원이면 서버가 Long 최댓값을 주므로 '무제한' 으로 보인다.
 *
 * @example formatSendCount(12) => '12건' / formatSendCount(9223372036854775807) => '무제한'
 */
export const formatSendCount = (count?: number | null): string =>
  (count ?? 0) >= 1e12 ? "무제한" : `${formatCommaNumber(count ?? 0)}건`;
